import { Box, Grid } from '@mui/material';
import { FormikHandlers, FormikHelpers, FormikState } from 'formik';
import { useMemo, useState, ReactElement } from 'react';

import { useGetStatesQuery } from '../../../../api/reference-data';
import { Address } from '../../../../domain/address';
import { FormikAutoCompleteAddressField } from '../../../../general/inputs/formik-auto-complete-address-field';
import { FormikAutoCompleteField } from '../../../../general/inputs/formik-auto-complete-field';
import { FormikTextField } from '../../../../general/inputs/formik-text-field';

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: FormikHandlers & FormikState<any> & FormikHelpers<any>;
  readOnly?: boolean;
  objectPrefix?: objectPrefix;
  // When true, marks the street address inputs as data-public for LogRocket.
  // Use for business addresses; leave default (false) for personal/officer addresses.
  isPublic?: boolean;
};

type objectPrefix = 'personalInfo';

export const isNonEmptyCoastAddress = (option: Address) => {
  return Object.values(option).some((value) => value);
};

function AddressFormFieldsWithAutocomplete({ formik, readOnly, objectPrefix, isPublic = false }: Props): ReactElement {
  const { data: states } = useGetStatesQuery();
  const streetAddressLabel = useMemo(() => {
    if (objectPrefix === 'personalInfo') {
      return 'Residential Street Address';
    }

    return 'Street Address';
  }, [objectPrefix]);
  const initialShowAddressFields = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
    const address: Address = objectPrefix ? formik.values[objectPrefix].address : formik.values.address;
    return isNonEmptyCoastAddress(address);
  }, [objectPrefix, formik]);
  const [showAddressFields, setShowAddressFields] = useState(initialShowAddressFields);

  const prefix = objectPrefix ? `${objectPrefix}.` : '';
  return (
    <>
      <FormikAutoCompleteAddressField
        formik={formik}
        id="streetAddress"
        label={streetAddressLabel}
        name={`${prefix}address.streetAddress`}
        objectPrefix={objectPrefix}
        readOnly={readOnly}
        onChange={() => setShowAddressFields(true)}
        isPublic={isPublic}
      />
      {showAddressFields && (
        <Box>
          <FormikTextField
            formik={formik}
            id="streetAddress2"
            name={`${prefix}address.streetAddress2`}
            label="Unit or C/O"
            placeholder="Unit or C/O"
            readOnly={readOnly}
            data-public={isPublic || undefined}
          />

          <Grid container spacing={2}>
            <Grid item xs={4}>
              <FormikTextField
                formik={formik}
                id="city"
                name={`${prefix}address.city`}
                label="City"
                placeholder="City"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={4}>
              <FormikAutoCompleteField
                formik={formik}
                id="state"
                name={`${prefix}address.state`}
                fullWidth
                label="State"
                options={states ?? []}
                readOnly={readOnly}
                autoSelect
                autoHighlight
              />
            </Grid>

            <Grid item xs={4}>
              <FormikTextField
                formik={formik}
                id="zip"
                name={`${prefix}address.zip`}
                label="Zip"
                placeholder="Zip"
                readOnly={readOnly}
                inputProps={{ inputMode: 'numeric' }}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
}

export { AddressFormFieldsWithAutocomplete };
